import apiService from '../apiService';
import { fetchAllPaginatedList, unwrapListRows } from '@/utils/paginatedFetch';

const DEPARTMENTS_URL = '/api/admin/departments';
const DEPARTMENT_PAGE_SIZE = 100;

const clampPerPage = (params = {}) => {
    const safeParams = { ...params };
    if (Number(safeParams.per_page) > DEPARTMENT_PAGE_SIZE) {
        safeParams.per_page = DEPARTMENT_PAGE_SIZE;
    }
    return safeParams;
};

/**
 * 獲取事業部列表（分頁，供列表頁捲動載入）
 * @param {Object} params - 查詢參數
 * @returns {Promise} API 響應
 */
export const getDepartments = async (params = {}) => {
    return apiService.GET(DEPARTMENTS_URL, clampPerPage(params));
};

/**
 * 取得所有事業部（自動翻頁合併，供下拉選單使用）
 * @param {Object} params - 額外篩選（如 is_active）
 * @returns {Promise<Array>} 事業部列陣列
 */
export const fetchAllDepartmentRows = async (params = {}) => {
    const response = await apiService.GET(DEPARTMENTS_URL, {
        ...params,
        all: 1
    });
    const rows = unwrapListRows(response);
    if (rows.length > 0) {
        return rows;
    }

    return fetchAllPaginatedList(
        ({ page, per_page }) => getDepartments({ ...params, page, per_page }),
        DEPARTMENT_PAGE_SIZE
    );
};

/**
 * @param {Array} rows
 * @param {{ valueAsString?: boolean }} options
 */
export const toDepartmentOptions = (rows, { valueAsString = false } = {}) => {
    const map = new Map();
    for (const department of Array.isArray(rows) ? rows : []) {
        if (department?.id == null) continue;
        const value = valueAsString
            ? String(department.id)
            : Number(department.id);
        if (!map.has(value)) {
            map.set(value, {
                label: department.name ?? `#${value}`,
                value
            });
        }
    }
    return [...map.values()];
};

/**
 * 獲取單一事業部詳情
 * @param {number} departmentId - 事業部 ID
 * @returns {Promise} API 響應
 */
export const getDepartmentDetail = async (departmentId) => {
    const url = `/api/admin/departments/${departmentId}`;
    return apiService.GET(url);
};

/**
 * 新增事業部
 * @param {Object} data - 事業部資料
 * @returns {Promise} API 響應
 */
export const createDepartment = async (data) => {
    const url = '/api/admin/departments';
    return apiService.POST(url, data);
};

/**
 * 更新事業部
 * @param {number} departmentId - 事業部 ID
 * @param {Object} data - 更新資料
 * @returns {Promise} API 響應
 */
export const updateDepartment = async (departmentId, data) => {
    const url = `/api/admin/departments/${departmentId}`;
    return apiService.PUT(url, data);
};

/**
 * 刪除事業部
 * @param {number} departmentId - 事業部 ID
 * @returns {Promise} API 響應
 */
export const deleteDepartment = async (departmentId) => {
    const url = `/api/admin/departments/${departmentId}`;
    return apiService.DELETE(url);
};

/**
 * 取得事業部組織架構圖
 * @param {number} departmentId - 事業部 ID
 * @returns {Promise} API 響應
 */
export const getDepartmentOrganizationChart = async (
    departmentId,
    type = 'referral'
) => {
    const url = `/api/admin/departments/${departmentId}/organization-chart?type=${type}`;
    return apiService.GET(url);
};

/**
 * 轉移事業部成員
 * @param {Object} data - 轉移資料 { from_department_id, to_department_id }
 * @returns {Promise} API 響應
 */
export const transferMembers = async (data) => {
    const url = '/api/admin/departments/transfer-members';
    return apiService.POST(url, data);
};

/**
 * 檢查事業部成員關係
 * @param {number} departmentId - 事業部 ID
 * @returns {Promise} API 響應
 */
export const checkDepartmentRelations = async (departmentId) => {
    const url = `/api/admin/departments/${departmentId}/check-relations`;
    return apiService.GET(url);
};
